import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Users, UserPlus, Download, Upload, Trash2, ShieldCheck, X,
  CheckCircle2, XCircle, Loader2, Clock, Edit3, FileText,
} from "lucide-react";
import { API } from "@/lib/auth";

const CUSTOMER_TYPES = [
  { v: "salon_client",       l: "Salon client" },
  { v: "training_student",   l: "Training student" },
  { v: "trade_customer",     l: "Trade customer" },
  { v: "general_subscriber", l: "General subscriber" },
];

const CONSENT_SOURCES = [
  "old_website", "booking_form", "trade_account", "course_booking",
  "welcome_popup", "quiz", "pamper_party", "footer_signup",
  "manual_add", "csv_import", "other",
];

export default function CustomerManager() {
  const [tab, setTab] = useState("list");
  const tabs = [
    { k: "list",   l: "Customers", Icon: Users },
    { k: "import", l: "Import",    Icon: Upload },
    { k: "audit",  l: "Audit log", Icon: FileText },
  ];
  return (
    <div className="space-y-6" data-testid="admin-customers-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Customer database</div>
          <p className="text-sm text-[hsl(var(--onyx)/0.6)] mt-1 max-w-2xl">
            GDPR-compliant customer records with consent tracking, import from CSV, audit trail and
            token-based unsubscribe. Only opted-in customers receive marketing.
          </p>
        </div>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {tabs.map(({ k, l, Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`customer-tab-${k}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
              tab === k
                ? "bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))]"
                : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--burgundy))]"
            }`}
          >
            <Icon size={12} /> {l}
          </button>
        ))}
      </div>
      {tab === "list"   && <CustomerList />}
      {tab === "import" && <CustomerImport />}
      {tab === "audit"  && <AuditLog />}
    </div>
  );
}


// ================================================================
// List + filters + inline drawer for add/edit
// ================================================================
function CustomerList() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterConsent, setFilterConsent] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [drawer, setDrawer] = useState(null); // "new" | customer object

  const load = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (filterType) params.customer_type = filterType;
      if (filterConsent) params.consent = filterConsent;
      const r = await axios.get(`${API}/admin/customers`, { params });
      setData(r.data);
      setSelected(new Set());
    } catch (e) {
      toast.error("Could not load customers");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType, filterConsent]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === (data?.customers?.length || 0)) setSelected(new Set());
    else setSelected(new Set((data?.customers || []).map((c) => c.id)));
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} customer${selected.size === 1 ? "" : "s"}? They'll also be added to the suppression list to prevent re-import.`)) return;
    try {
      await axios.post(`${API}/admin/customers/bulk-delete`, { ids: [...selected] });
      toast.success(`Deleted ${selected.size}`);
      await load();
    } catch { toast.error("Bulk delete failed"); }
  };

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/customers/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (!data) return <div className="py-10 flex items-center justify-center text-[hsl(var(--onyx)/0.4)]"><Loader2 className="animate-spin" size={18} /></div>;

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat Icon={Users}        label="Total"          value={data.stats.total} />
        <Stat Icon={CheckCircle2} label="Marketing OK"   value={data.stats.consented} accent />
        <Stat Icon={XCircle}      label="Unsubscribed"   value={data.stats.unsubscribed} />
        <Stat Icon={ShieldCheck}  label="No consent"     value={data.stats.no_consent} />
      </div>

      {/* Toolbar */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-4 flex flex-wrap items-center gap-3">
        <form onSubmit={onSearch} className="flex-1 min-w-[220px] relative">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--onyx)/0.4)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            data-testid="customer-search"
            className="w-full h-10 pl-10 pr-4 rounded-full bg-white border border-black/10 text-sm outline-none focus:border-[hsl(var(--burgundy))]"
          />
        </form>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="customer-filter-type"
          className="h-10 px-4 rounded-full bg-white border border-black/10 text-sm outline-none focus:border-[hsl(var(--burgundy))]">
          <option value="">All types</option>
          {CUSTOMER_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
        <select value={filterConsent} onChange={(e) => setFilterConsent(e.target.value)} data-testid="customer-filter-consent"
          className="h-10 px-4 rounded-full bg-white border border-black/10 text-sm outline-none focus:border-[hsl(var(--burgundy))]">
          <option value="">Any consent</option>
          <option value="yes">Marketing OK</option>
          <option value="no">No consent</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
        <button onClick={() => setDrawer("new")} data-testid="customer-add-btn" className="btn-pill btn-primary !text-xs">
          <UserPlus size={14} /> Add customer
        </button>
        <button onClick={downloadCsv} data-testid="customer-export-btn" className="btn-pill btn-outline !text-xs">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="rounded-2xl bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] p-3 flex items-center justify-between gap-3">
          <div className="text-sm">{selected.size} selected</div>
          <button onClick={bulkDelete} data-testid="customer-bulk-delete" className="btn-pill bg-red-500 text-white !text-xs hover:opacity-90">
            <Trash2 size={14} /> Delete selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-blush/40 text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">
              <tr>
                <th className="p-3 w-8">
                  <input type="checkbox"
                    checked={selected.size === data.customers.length && data.customers.length > 0}
                    onChange={toggleAll}
                    data-testid="customer-select-all" />
                </th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Consent</th>
                <th className="p-3 text-left">Source</th>
                <th className="p-3 text-left">Added</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.customers.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-[hsl(var(--onyx)/0.5)]">No customers match your filters.</td></tr>
              )}
              {data.customers.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-t border-[hsl(var(--onyx)/0.06)] hover:bg-blush/20">
                  <td className="p-3"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} /></td>
                  <td className="p-3 font-medium">{c.name}</td>
                  <td className="p-3 text-[hsl(var(--onyx)/0.75)] break-all">{c.email}</td>
                  <td className="p-3">
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-blush/50 text-[hsl(var(--burgundy))]">
                      {(CUSTOMER_TYPES.find((t) => t.v === c.customer_type)?.l) || c.customer_type}
                    </span>
                  </td>
                  <td className="p-3">
                    {c.unsubscribed ? (
                      <span className="text-red-500 inline-flex items-center gap-1"><XCircle size={12} /> Unsubbed</span>
                    ) : c.marketing_consent ? (
                      <span className="text-green-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Yes</span>
                    ) : (
                      <span className="text-[hsl(var(--onyx)/0.5)]">No</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-[hsl(var(--onyx)/0.55)]">{c.consent_source || "—"}</td>
                  <td className="p-3 text-xs text-[hsl(var(--onyx)/0.55)]">{c.created_at?.slice(0, 10)}</td>
                  <td className="p-3">
                    <button onClick={() => setDrawer(c)} className="text-xs text-[hsl(var(--burgundy))] hover:underline inline-flex items-center gap-1">
                      <Edit3 size={12} /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {drawer && <CustomerDrawer initial={drawer === "new" ? null : drawer} onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); load(); }} />}
    </div>
  );
}


// ---------- Add / Edit drawer ----------
function CustomerDrawer({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    email: initial?.email || "",
    phone: initial?.phone || "",
    marketing_consent: initial?.marketing_consent || false,
    consent_date: initial?.consent_date || new Date().toISOString().slice(0, 10),
    consent_source: initial?.consent_source || "manual_add",
    customer_type: initial?.customer_type || "general_subscriber",
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/customers`, form);
        toast.success("Customer added");
      } else {
        await axios.put(`${API}/admin/customers/${initial.id}`, form);
        toast.success("Customer updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this customer? Their email will be added to the suppression list.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/customers/${initial.id}`);
      toast.success("Customer deleted");
      onSaved();
    } catch { toast.error("Delete failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40" onClick={onClose} data-testid="customer-drawer">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[hsl(var(--onyx)/0.06)] flex items-center justify-between">
          <div>
            <div className="overline">{isNew ? "Add customer" : "Edit customer"}</div>
            <h3 className="font-display text-xl mt-1">{isNew ? "New record" : initial.name}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-blush/40 flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <F label="Full name">
            <input value={form.name} onChange={(e) => patch("name", e.target.value)} data-testid="customer-form-name" className={inp} />
          </F>
          <F label="Email">
            <input type="email" value={form.email} onChange={(e) => patch("email", e.target.value)} data-testid="customer-form-email" className={inp} />
          </F>
          <F label="Phone">
            <input value={form.phone} onChange={(e) => patch("phone", e.target.value)} data-testid="customer-form-phone" className={inp} />
          </F>
          <F label="Customer type">
            <select value={form.customer_type} onChange={(e) => patch("customer_type", e.target.value)} data-testid="customer-form-type" className={inp}>
              {CUSTOMER_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </F>
          <label className="flex items-start gap-3 p-4 rounded-2xl bg-blush/30 cursor-pointer">
            <input type="checkbox" checked={form.marketing_consent} onChange={(e) => patch("marketing_consent", e.target.checked)} data-testid="customer-form-consent" className="mt-1 w-4 h-4" />
            <span className="text-sm text-[hsl(var(--onyx)/0.8)]">
              <strong>Marketing consent given</strong> — only tick if you can evidence they opted in.
            </span>
          </label>
          <F label="Consent date">
            <input type="date" value={form.consent_date || ""} onChange={(e) => patch("consent_date", e.target.value)} className={inp} />
          </F>
          <F label="Consent source">
            <select value={form.consent_source || ""} onChange={(e) => patch("consent_source", e.target.value)} className={inp}>
              {CONSENT_SOURCES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </F>
          <F label="Notes">
            <textarea rows={3} value={form.notes} onChange={(e) => patch("notes", e.target.value)} className={`${inp} !h-auto !rounded-2xl py-3`} />
          </F>
          <div className="flex gap-3 flex-wrap pt-4">
            <button onClick={save} disabled={busy} data-testid="customer-form-save" className="btn-pill btn-primary !text-xs disabled:opacity-50">
              {busy ? "Saving…" : (isNew ? "Add customer" : "Save changes")}
            </button>
            {!isNew && (
              <button onClick={remove} className="btn-pill bg-red-50 text-red-600 !text-xs hover:bg-red-100 border-none">
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ================================================================
// CSV Import
// ================================================================
function CustomerImport() {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parse = (text) => {
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) { toast.error("CSV needs a header row + at least one data row"); return; }
    const hdr = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
    const data = lines.slice(1).map((l) => {
      // basic csv parser (no quoted-commas support for simplicity)
      const cells = l.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
      return Object.fromEntries(hdr.map((h, i) => [h, cells[i] || ""]));
    });
    setHeaders(hdr);
    setRows(data);
    // auto-map columns by name
    const auto = {};
    for (const h of hdr) {
      const key = h.toLowerCase().replace(/\s+/g, "_");
      if (["name", "full_name", "fullname"].includes(key)) auto.name = h;
      else if (["email", "email_address"].includes(key)) auto.email = h;
      else if (["phone", "mobile", "phone_number"].includes(key)) auto.phone = h;
      else if (["consent", "marketing_consent"].includes(key)) auto.marketing_consent = h;
      else if (["consent_date", "date_of_consent"].includes(key)) auto.consent_date = h;
      else if (["source", "consent_source"].includes(key)) auto.consent_source = h;
      else if (["type", "customer_type"].includes(key)) auto.customer_type = h;
      else if (["notes"].includes(key)) auto.notes = h;
    }
    setMapping(auto);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => parse(reader.result);
    reader.readAsText(file);
    e.target.value = "";
  };

  const doImport = async () => {
    if (!rows.length) return;
    if (!mapping.email) { toast.error("You must map an Email column"); return; }
    setBusy(true);
    try {
      const mapped = rows.map((r) => ({
        name: r[mapping.name || ""] || "",
        email: r[mapping.email] || "",
        phone: r[mapping.phone || ""] || "",
        marketing_consent: /^(true|yes|1|opted_in)$/i.test(r[mapping.marketing_consent || ""] || ""),
        consent_date: r[mapping.consent_date || ""] || null,
        consent_source: r[mapping.consent_source || ""] || "csv_import",
        customer_type: r[mapping.customer_type || ""] || "general_subscriber",
        notes: r[mapping.notes || ""] || "",
      }));
      const r = await axios.post(`${API}/admin/customers/import`, { rows: mapped });
      setResult(r.data);
      toast.success(`Imported ${r.data.inserted} customers`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
        <div className="w-12 h-12 rounded-full bg-blush flex items-center justify-center">
          <Upload size={18} className="text-[hsl(var(--burgundy))]" />
        </div>
        <h3 className="font-display text-2xl mt-4">Import Existing Customers</h3>
        <p className="text-sm text-[hsl(var(--onyx)/0.65)] mt-2 leading-relaxed max-w-2xl">
          Upload a CSV file from your previous website. We'll auto-map columns like <em>email</em>, <em>name</em>, <em>phone</em>, <em>marketing_consent</em>, <em>consent_date</em>, <em>consent_source</em> and <em>customer_type</em> — you can adjust the mapping before importing.
          <br />
          <strong>GDPR:</strong> customers are only marked opted-in for marketing if the consent column contains <code>true / yes / 1 / opted_in</code>. Duplicates and suppressed emails are skipped.
        </p>
        <label className="mt-6 inline-block cursor-pointer btn-pill btn-primary !text-xs">
          <Upload size={14} /> Choose CSV file
          <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="customer-csv-file" className="hidden" />
        </label>
      </div>

      {rows.length > 0 && (
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
          <div className="overline">Column mapping — {rows.length} rows detected</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {["name","email","phone","marketing_consent","consent_date","consent_source","customer_type","notes"].map((field) => (
              <label key={field} className="block">
                <span className="text-xs text-[hsl(var(--onyx)/0.6)]">{field.replace(/_/g, " ")} {field === "email" && <span className="text-[hsl(var(--burgundy))]">*</span>}</span>
                <select value={mapping[field] || ""} onChange={(e) => setMapping({ ...mapping, [field]: e.target.value })}
                  data-testid={`csv-map-${field}`}
                  className="mt-1 w-full h-9 px-3 rounded-full bg-white border border-black/10 text-xs outline-none focus:border-[hsl(var(--burgundy))]">
                  <option value="">— skip —</option>
                  {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </label>
            ))}
          </div>
          <button onClick={doImport} disabled={busy} data-testid="csv-import-btn" className="btn-pill btn-primary !text-xs disabled:opacity-50">
            {busy ? "Importing…" : `Import ${rows.length} rows`}
          </button>
        </div>
      )}

      {result && (
        <div className="rounded-3xl bg-white border border-green-200 p-6 space-y-3" data-testid="csv-import-result">
          <div className="overline text-green-700">Import complete</div>
          <div className="grid gap-2 sm:grid-cols-4 text-sm">
            <div>✅ Inserted: <strong>{result.inserted}</strong></div>
            <div>👥 Duplicates skipped: <strong>{result.duplicates?.length || 0}</strong></div>
            <div>🚫 Suppressed skipped: <strong>{result.suppressed?.length || 0}</strong></div>
            <div>❌ Invalid: <strong>{result.invalid?.length || 0}</strong></div>
          </div>
        </div>
      )}
    </div>
  );
}


// ================================================================
// Audit log
// ================================================================
function AuditLog() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/customers/audit`).then((r) => setRows(r.data.rows)).catch(() => toast.error("Load failed"));
  }, []);
  if (!rows) return <div className="py-10 flex items-center justify-center text-[hsl(var(--onyx)/0.4)]"><Loader2 className="animate-spin" size={18} /></div>;
  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-blush/40 text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">
          <tr>
            <th className="p-3 text-left">When</th>
            <th className="p-3 text-left">Action</th>
            <th className="p-3 text-left">Admin</th>
            <th className="p-3 text-left">Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[hsl(var(--onyx)/0.5)]">No activity yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[hsl(var(--onyx)/0.06)]">
              <td className="p-3 text-xs text-[hsl(var(--onyx)/0.6)] whitespace-nowrap"><Clock size={10} className="inline mr-1" />{new Date(r.at).toLocaleString()}</td>
              <td className="p-3"><span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-blush/50 text-[hsl(var(--burgundy))]">{r.action}</span></td>
              <td className="p-3 text-xs text-[hsl(var(--onyx)/0.7)]">{r.admin_email}</td>
              <td className="p-3 text-xs text-[hsl(var(--onyx)/0.6)] font-mono">{JSON.stringify(r.detail)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ---------- Little helpers ----------
const inp = "w-full h-10 rounded-full bg-white border border-black/10 px-4 text-sm outline-none focus:border-[hsl(var(--burgundy))]";
function F({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-[hsl(var(--onyx)/0.6)]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function Stat({ Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl bg-white border p-4 ${accent ? "border-[hsl(var(--burgundy)/0.2)] ring-1 ring-[hsl(var(--burgundy)/0.1)]" : "border-[hsl(var(--onyx)/0.06)]"}`}>
      <div className="flex items-center gap-2">
        <Icon size={12} className="text-[hsl(var(--burgundy))]" />
        <div className="overline">{label}</div>
      </div>
      <div className="font-display text-2xl mt-2">{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}
